import { Button, Card, Link, Stack, Typography } from "@mui/material"
import Grid from '@mui/material/Grid2';
import { useEffect, useRef, useState } from "react"
import { BoxImg, linkStyle } from "./style"
import { InfoDetailsDrawer } from "../InfoDetailsDrawer"
import { IProjectResponse } from "../../../../../api/types";
import { profileApi } from "../../../../../api/profile"
import { routesProfile } from "../../../../../consts";
import { useNavigate } from "react-router-dom";
import { MouseEvent } from "react";
import { useLikeProjectMutation, useSubscribeProjectMutation, useUnlikeProjectMutation, useUnsubscribeProjectMutation } from "../../../../../api/projects";
import { toast } from "react-toastify";
import modalEmitter from "../../../../ModalComponent/modalEmitter";
import { JoinProjectModalBody, openLeaveProjectModal, ProjectMembersModalBody, openPlatformReportModal } from "../../../../Modals";
import { ProjectExternalLinksSection } from "../../../ProjectExternalLinksSection";
import { formatNumber } from "../../../../../common/FormatNumber";
import { COLORS } from "../../../../../styles/variables";
import { ProjectCoverImage } from "../../../../common/ProjectCoverImage";
import { EntityImagePreviewModal } from "../../../../common/EntityImagePreviewModal";
import { useEntityImagePreview } from "../../../../common/useEntityImagePreview";
import { IconProjectLink } from "../../../../../icons/IconProjectLink";
import { websiteHref } from "../../../../../utils/common";
import { ProjectLikeControl } from "../../../ProjectLikeControl";
import { ProjectTeamSection } from "../../../ProjectTeamSection";
import { ParticipatingTeamsSection } from "../../../ParticipatingTeamsSection";
import { SourceSocialOrderSection } from '../../../SourceSocialOrderSection'
import { useSettledToggleNetwork } from "../../../../../hooks/useSettledToggleNetwork"

interface MobileInfoProps {
    project: IProjectResponse
}

export const MobileInfo = ({project}: MobileInfoProps) => {
    const navigation = useNavigate();
    const { data: me } = profileApi.useGetProfileQuery()
    const permissions = project.viewer?.permissions
    const canEdit = permissions?.can_edit_content || permissions?.can_moderate
    const canViewTeamSpace = permissions?.can_view_team_space === true
    const isAuthor = me?.id !== undefined && me?.id === project.author.id;
    const [subscribeProject, { isLoading: isSubscribeLoading }] = useSubscribeProjectMutation()
    const [unsubscribeProject, { isLoading: isUnsubscribeLoading }] = useUnsubscribeProjectMutation()
    const [likeProject, likeState] = useLikeProjectMutation()
    const [unlikeProject, unlikeState] = useUnlikeProjectMutation()
    const viewsCount = Number(project.counters?.views ?? 0)
    const membersCount = Number(project.counters?.members ?? 0)
    const [isFollowed, setIsFollowed] = useState(project.is_followed === true)
    const [subscribersCount, setSubscribersCount] = useState(Number(project.counters?.subscribers ?? 0))
    const [isLiked, setIsLiked] = useState(project.viewer?.is_liked === true)
    const [likesCount, setLikesCount] = useState(Number(project.counters.likes ?? 0))
    const subscribeSettle = useSettledToggleNetwork()
    const likeSettle = useSettledToggleNetwork()
    const subscribeBurstRef = useRef<boolean | null>(null)
    const likeBurstRef = useRef<{ value: boolean; count: number } | null>(null)
    const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false)
    const {
        canPreview: canPreviewCover,
        openPreview: openCoverPreview,
        closePreview: closeCoverPreview,
        galleryOpen: coverGalleryOpen,
        galleryImages: coverGalleryImages,
    } = useEntityImagePreview(project.image)

    useEffect(() => {
        setIsFollowed(project.is_followed === true)
        setSubscribersCount(Number(project.counters?.subscribers ?? 0))
    }, [project.id, project.is_followed, project.counters?.subscribers])

    useEffect(() => {
        setIsLiked(project.viewer?.is_liked === true)
        setLikesCount(Number(project.counters.likes ?? 0))
    }, [project.id, project.viewer?.is_liked, project.counters?.likes])

    const toggleDrawer = (state: boolean) => () => { 
        setOpenDetailsDrawer(state) 
    }
    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        navigation(`/${routesProfile.PROJECTS.path}/${project.id}/edit`)
    };

    const handleJoinProject = () => {
        modalEmitter.emitOpenModal({
            dialog: {
                title: "Присоединиться к проекту",
                body: <JoinProjectModalBody projectId={project.id} />,
            },
            closeAtion: { visible: false },
            confirmAtion: { visible: false },
        })
    }

    const handleToggleSubscribe = () => {
        if (isSubscribeLoading || isUnsubscribeLoading) return
        if (subscribeBurstRef.current === null) {
            subscribeBurstRef.current = isFollowed
        }
        const next = !isFollowed
        setIsFollowed(next)
        setSubscribersCount((n) => Math.max(0, n + (isFollowed ? -1 : 1)))

        subscribeSettle.schedule({
            desired: next,
            commit: async (desired) => {
                subscribeBurstRef.current = null
                if (desired) {
                    await subscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
                    toast.success("Вы подписались на проект")
                } else {
                    await unsubscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
                    toast.success("Вы отписались от проекта")
                }
            },
            onSkip: () => {
                subscribeBurstRef.current = null
            },
            onError: (baseline) => {
                subscribeBurstRef.current = null
                setIsFollowed(baseline)
                setSubscribersCount(Number(project.counters?.subscribers ?? 0))
                toast.error("Не удалось изменить подписку")
            },
        })
    }

    const teamPreview = (project.team_preview ?? []).slice(0, 5)
    const participatingTeams = project.participating_teams ?? []

    const handleToggleLike = () => {
        if (likeState.isLoading || unlikeState.isLoading) return
        if (!me?.id || !project.viewer) {
            toast.info("Войдите, чтобы ставить лайки")
            return
        }
        if (!likeBurstRef.current) {
            likeBurstRef.current = { value: isLiked, count: likesCount }
        }
        const next = !isLiked
        setIsLiked(next)
        setLikesCount((n) => Math.max(0, n + (isLiked ? -1 : 1)))

        likeSettle.schedule({
            desired: next,
            commit: async (desired) => {
                likeBurstRef.current = null
                if (desired) {
                    await likeProject({ projectId: project.id, slug: project.slug }).unwrap()
                } else {
                    await unlikeProject({ projectId: project.id, slug: project.slug }).unwrap()
                }
            },
            onSkip: () => {
                likeBurstRef.current = null
            },
            onError: () => {
                const burst = likeBurstRef.current
                likeBurstRef.current = null
                if (burst) {
                    setIsLiked(burst.value)
                    setLikesCount(burst.count)
                }
                toast.error('Не удалось изменить лайк')
            },
        })
    }

    const openMembersModal = () => {
        modalEmitter.emitOpenModal({
            dialog: {
                title: 'Вся команда',
                body: <ProjectMembersModalBody projectId={project.id} />,
                modalToCentrPosition: true,
            },
        })
    }

    const externalLinks = project.external_links ?? []

    return (
        <>
            <Card variant="primaryAdaptiveCard" sx={{ padding: 1.5 }}>
                <Stack 
                    direction="row" 
                    gap={1} 
                    justifyContent="space-between" 
                    mb={1}
                    width="100%"
                >
                    <ProjectCoverImage
                        src={project.image}
                        alt="Image"
                        sx={BoxImg}
                        onClick={canPreviewCover ? openCoverPreview : undefined}
                    />
                    {canEdit ? (
                        <Button variant="follow" onClick={handleEdit}>Редактировать</Button>
                    ) : (
                        <Button
                            variant="follow"
                            onClick={handleToggleSubscribe}
                            disabled={isSubscribeLoading || isUnsubscribeLoading}
                        >
                            {isFollowed ? "Отписаться" : "Подписаться"}
                        </Button>
                    )}
                </Stack>
                <Stack mb={2} gap={1} alignItems="flex-start">
                    {project.website?.trim() && (
                        <Stack direction="row" alignItems="center" gap={1.5} width="100%" sx={{ minWidth: 0 }}>
                            <IconProjectLink />
                            <Link
                                component="a"
                                href={websiteHref(project.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="black-14-light"
                                noWrap
                                sx={linkStyle}
                            >
                                {project.website}
                            </Link>
                        </Stack>
                    )}
                    <ProjectExternalLinksSection links={externalLinks} />
                    <Grid width="100%" container spacing={0.5}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="black-14-medium" sx={{ lineHeight: "18px" }}>Подписчиков: {formatNumber(subscribersCount)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="black-14-medium" sx={{ lineHeight: "18px" }}>Просмотров: {formatNumber(viewsCount)}</Typography>
                        </Grid>
                    </Grid>
                    <SourceSocialOrderSection sourceSocialOrder={project.source_social_order} />
                    {canViewTeamSpace ? (
                        <Button
                            variant="follow"
                            onClick={() => navigation(`/${routesProfile.PROJECTS.path}/${project.slug}/team-space`)}
                            sx={{ alignSelf: 'stretch', maxHeight: 32 }}
                        >
                            Командное пространство
                        </Button>
                    ) : null}
                    <ProjectTeamSection
                        members={teamPreview}
                        membersTotal={membersCount}
                        onOpenAllMembers={openMembersModal}
                    />
                    <ParticipatingTeamsSection teams={participatingTeams} />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Stack flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Button
                            variant="action"
                            onClick={toggleDrawer(true)}
                            disableRipple
                            disableFocusRipple
                            sx={{ color: COLORS.CERULEAN }}
                        >
                            Подробнее
                        </Button>
                        {!isAuthor && (
                            project.is_joined ? (
                                <Button
                                    variant="danger"
                                    onClick={() => openLeaveProjectModal(project.id)}
                                >
                                    Выбыть
                                </Button>
                            ) : (
                                <Button variant="action" onClick={handleJoinProject}>
                                    Присоединиться
                                </Button>
                            )
                        )}
                        {!isAuthor ? (
                            <Button
                                variant="text"
                                size="small"
                                onClick={() =>
                                    openPlatformReportModal({
                                        targetType: 'project',
                                        targetId: project.id,
                                        dialogTitle: 'Пожаловаться на проект',
                                    })
                                }
                            >
                                Пожаловаться
                            </Button>
                        ) : null}
                    </Stack>
                    <ProjectLikeControl
                        projectId={project.id}
                        isLiked={isLiked}
                        likesCount={likesCount}
                        likesPreview={project.likes_preview}
                        onToggleLike={handleToggleLike}
                        loading={likeState.isLoading || unlikeState.isLoading}
                    />
                </Stack>
            </Card>

            <InfoDetailsDrawer
                open={openDetailsDrawer}
                onClose={toggleDrawer(false)}
                project={project}
                onOpenAllMembers={openMembersModal}
            />
            <EntityImagePreviewModal
                open={coverGalleryOpen}
                onClose={closeCoverPreview}
                images={coverGalleryImages}
            />
        </>
    )
}
